import { useContext, useMemo, useState } from "react";
import "../FoodMenu.css";
import { Button, GripVertical, Input, Plus, SearchCheckList, Select, Trash2 } from "../../../ui";
import { Context } from "../../../../context/Context";
import { PRICING_NAME_LABELS, calculateEffectivePrice, pricingEntryError } from "../../../../utils/menuPricing";

const TEXT = {
  ar: {
    addTitle: "إضافة صنف جديد",
    editTitle: "تعديل الصنف",
    nameAr: "اسم الصنف عربي",
    nameEn: "اسم الصنف انجليزي",
    descAr: "الوصف عربي",
    descEn: "الوصف انجليزي",
    allergensAr: "مسببات الحساسية عربي",
    allergensEn: "مسببات الحساسية انجليزي",
    caloriesAr: "السعرات عربي",
    caloriesEn: "السعرات انجليزي",
    isBox: "هل هو بوكس؟",
    prices: "الأسعار",
    pricesHint: "نفس الأسعار لكل الفروع التي تستخدم قائمة الطعام هذه. \"الأساسي\" هو السعر الافتراضي.",
    priceName: "اسم السعر",
    customName: "اسم مخصص",
    custom: "اسم آخر…",
    price: "السعر",
    discountType: "الخصم",
    noDiscount: "بدون خصم",
    amount: "مبلغ",
    percentage: "نسبة %",
    discountValue: "قيمة الخصم",
    effective: "بعد الخصم",
    linkedAdditionsTitle: "الإضافات",
    linkedAdditionsEmpty: "لا توجد إضافات لهذا المطعم، قم بإضافتها من زر إضافات القائمة",
    linkedAdditionsHint: "اختر مجموعات الإضافات المرتبطة بهذا الصنف (من إضافات المطعم فقط)",
    searchPlaceholder: "ابحث في إضافات القائمة",
    options: "خيارات",
    mandatory: "إلزامية",
    optional: "اختيارية",
    free: "مجانًا",
    selectedGroups: "المجموعات المختارة",
    noSelectedGroups: "لم يتم اختيار أي مجموعة إضافات بعد",
    save: "حفظ",
    cancel: "إلغاء",
    nameRequired: "اسم الصنف مطلوب",
  },
  en: {
    addTitle: "Add item",
    editTitle: "Edit item",
    nameAr: "Arabic name",
    nameEn: "English name",
    descAr: "Arabic description",
    descEn: "English description",
    allergensAr: "Arabic allergens",
    allergensEn: "English allergens",
    caloriesAr: "Arabic calories",
    caloriesEn: "English calories",
    isBox: "Is it a box?",
    prices: "Prices",
    pricesHint: "Same prices for every branch using this menu. \"Base\" is the default price.",
    priceName: "Price name",
    customName: "Custom name",
    custom: "Other name…",
    price: "Price",
    discountType: "Discount",
    noDiscount: "No discount",
    amount: "Amount",
    percentage: "Percent %",
    discountValue: "Discount value",
    effective: "After discount",
    linkedAdditionsTitle: "Additions",
    linkedAdditionsEmpty: "No additions for this restaurant yet. Add them from Menu Additions",
    linkedAdditionsHint: "Select the addition groups linked to this item (this restaurant's additions only)",
    searchPlaceholder: "Search menu additions",
    options: "options",
    mandatory: "Mandatory",
    optional: "Optional",
    free: "Free",
    selectedGroups: "Selected groups",
    noSelectedGroups: "No addition groups selected yet",
    save: "Save",
    cancel: "Cancel",
    nameRequired: "Item name is required",
  },
};

const KNOWN_NAMES = Object.keys(PRICING_NAME_LABELS.en);
const CUSTOM = "__custom__";

// First validation problem of an item form (same rules as the backend), or "".
export function itemFormError(itemData, lang) {
  const t = TEXT[lang] || TEXT.ar;
  if (!itemData.name?.ar?.trim() && !itemData.name?.en?.trim()) return t.nameRequired;
  return (itemData.pricing || []).map((entry) => pricingEntryError(entry, lang)).find(Boolean) || "";
}

// Every editable item field (names, descriptions, allergens, calories, box,
// pricing[] with discounts, linked addition groups). Rendered by the per-card
// editor below and by the top-level Item actions, so both stay identical.
export function FoodItemFields({ itemData, setItemData, menuAdditions = [] }) {
  const { lang } = useContext(Context);
  const t = TEXT[lang] || TEXT.ar;
  const isArabic = lang === "ar";
  const [draggedAdditionIndex, setDraggedAdditionIndex] = useState(null);

  const nameOptions = [...KNOWN_NAMES.map((value) => ({ value, label: PRICING_NAME_LABELS[lang]?.[value] || value })), { value: CUSTOM, label: t.custom }];
  const updateField = (key, value) => setItemData({ ...itemData, [key]: value });
  const updateNestedField = (key, langKey, value) => setItemData({ ...itemData, [key]: { ...(itemData[key] || { ar: "", en: "" }), [langKey]: value } });

  const pricing = itemData.pricing || [];
  const updatePricing = (index, patch) => updateField("pricing", pricing.map((entry, i) => (i === index ? { ...entry, ...patch } : entry)));
  const addPricing = () => updateField("pricing", [...pricing, { name: pricing.some((p) => p.name === "base") ? "" : "base", price: "", discount: null, custom: false }]);
  const removePricing = (index) => updateField("pricing", pricing.filter((_, i) => i !== index));

  const getAdditionId = (group) => (typeof group?.addition === "string" ? group.addition : group?.addition?._id);
  const selectedAdditionIds = Array.from(new Set((itemData.additionGroups || []).map(getAdditionId).filter(Boolean)));
  const reindexGroups = (groups = []) => groups.map((group, index) => ({ ...group, order: index + 1 }));
  const updateLinkedAdditionGroups = (nextIds) => {
    const currentGroups = itemData.additionGroups || [];
    const currentIds = new Set(currentGroups.map(getAdditionId).filter(Boolean));
    const nextSet = new Set(nextIds);
    const nextGroups = currentGroups.filter((group) => nextSet.has(getAdditionId(group)));
    nextIds.forEach((id) => {
      if (!currentIds.has(id)) nextGroups.push({ addition: id });
    });
    updateField("additionGroups", reindexGroups(nextGroups));
  };
  const duplicateLinkedAdditionGroup = (groupIndex) => {
    const groups = [...(itemData.additionGroups || [])];
    if (!groups[groupIndex]) return;
    groups.splice(groupIndex + 1, 0, { addition: getAdditionId(groups[groupIndex]) });
    updateField("additionGroups", reindexGroups(groups));
  };
  const removeLinkedAdditionGroup = (groupIndex) => updateField("additionGroups", reindexGroups((itemData.additionGroups || []).filter((_, index) => index !== groupIndex)));
  const moveLinkedAdditionGroup = (fromIndex, toIndex) => {
    if (fromIndex === toIndex) return;
    const groups = [...(itemData.additionGroups || [])];
    const [moving] = groups.splice(fromIndex, 1);
    groups.splice(toIndex, 0, moving);
    updateField("additionGroups", reindexGroups(groups));
  };

  const getOptionName = (option) => option.name?.[lang] || option.name?.ar || option.name?.en || "-";
  const formatOptionPrice = (price) => (Number(price || 0) === 0 ? t.free : `+${Number(price)}`);
  const getAdditionTitle = (addition) => addition.title?.[lang] || addition.title?.ar || addition.title?.en || "-";

  const menuAdditionOptions = useMemo(
    () =>
      (menuAdditions || [])
        .filter((addition) => !addition.archivedAt && addition.isActive !== false)
        .map((addition) => {
          const preview = (addition.options || []).map((option) => `${getOptionName(option)} (${formatOptionPrice(option.price)})`).join(" | ");
          return {
            value: addition._id,
            label: `${getAdditionTitle(addition)} [${addition.shortName}] - ${addition.isMandatory ? t.mandatory : t.optional} - ${addition.options?.length || 0} ${t.options}${preview ? ` - ${preview}` : ""}`,
          };
        }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [lang, menuAdditions],
  );

  const selectedMenuAdditions = useMemo(
    () =>
      (itemData.additionGroups || [])
        .map((group, groupIndex) => {
          const addition = (menuAdditions || []).find((entry) => entry._id === getAdditionId(group)) || (typeof group.addition === "object" ? group.addition : null);
          return addition ? { group, groupIndex, addition } : null;
        })
        .filter(Boolean),
    [itemData.additionGroups, menuAdditions],
  );

  return (
    <>
        <div className="food-two-cols">
          <Input type="text" placeholder={t.nameAr} aria-label={t.nameAr} fullWidth={false} value={itemData.name?.ar || ""} onChange={(e) => updateNestedField("name", "ar", e.target.value)} />
          <Input type="text" placeholder={t.nameEn} aria-label={t.nameEn} fullWidth={false} value={itemData.name?.en || ""} onChange={(e) => updateNestedField("name", "en", e.target.value)} />
        </div>
        <div className="food-two-cols">
          <textarea placeholder={t.descAr} aria-label={t.descAr} value={itemData.description?.ar || ""} onChange={(e) => updateNestedField("description", "ar", e.target.value)} />
          <textarea placeholder={t.descEn} aria-label={t.descEn} value={itemData.description?.en || ""} onChange={(e) => updateNestedField("description", "en", e.target.value)} />
        </div>
        <div className="food-two-cols">
          <Input type="text" placeholder={t.allergensAr} aria-label={t.allergensAr} fullWidth={false} value={itemData.allergens?.ar || ""} onChange={(e) => updateNestedField("allergens", "ar", e.target.value)} />
          <Input type="text" placeholder={t.allergensEn} aria-label={t.allergensEn} fullWidth={false} value={itemData.allergens?.en || ""} onChange={(e) => updateNestedField("allergens", "en", e.target.value)} />
        </div>
        <div className="food-two-cols">
          <Input type="text" placeholder={t.caloriesAr} aria-label={t.caloriesAr} fullWidth={false} value={itemData.calories?.ar || ""} onChange={(e) => updateNestedField("calories", "ar", e.target.value)} />
          <Input type="text" placeholder={t.caloriesEn} aria-label={t.caloriesEn} fullWidth={false} value={itemData.calories?.en || ""} onChange={(e) => updateNestedField("calories", "en", e.target.value)} />
        </div>

        <label className="food-checkbox">
          <input type="checkbox" checked={itemData.isBox || false} onChange={(e) => updateField("isBox", e.target.checked)} />
          {t.isBox}
        </label>

        <div className="food-form-section">
          <div className="food-form-section-title">
            <div>
              <h4>{t.prices}</h4>
              <p className="food-form-section-note">{t.pricesHint}</p>
            </div>
            <Button type="button" variant="primary" appearance="soft" size="sm" rounded icon={<Plus size={16} />} aria-label={t.prices} onClick={addPricing} />
          </div>

          {pricing.map((entry, idx) => {
            const isCustom = entry.custom || (entry.name && !KNOWN_NAMES.includes(entry.name));
            const entryError = entry.price === "" ? "" : pricingEntryError(entry, lang);
            const effective = calculateEffectivePrice(entry.price, entry.discount);
            return (
              <div className="food-form-option food-pricing-row" key={idx}>
                <span className="food-order-badge">{idx + 1}</span>
                <Select
                  value={isCustom ? CUSTOM : entry.name}
                  placeholder={t.priceName}
                  fullWidth={false}
                  searchable
                  options={nameOptions}
                  onChange={(e) => (e.target.value === CUSTOM ? updatePricing(idx, { custom: true, name: isCustom ? entry.name : "" }) : updatePricing(idx, { custom: false, name: e.target.value }))}
                />
                {isCustom && <Input type="text" placeholder={t.customName} aria-label={t.customName} fullWidth={false} value={entry.name || ""} onChange={(e) => updatePricing(idx, { name: e.target.value })} />}
                <Input type="number" min="0" step="0.01" placeholder={t.price} aria-label={t.price} fullWidth={false} value={entry.price ?? ""} onChange={(e) => updatePricing(idx, { price: e.target.value === "" ? "" : Number(e.target.value) })} />
                <Select
                  value={entry.discount?.type || "none"}
                  fullWidth={false}
                  options={[
                    { value: "none", label: t.noDiscount },
                    { value: "amount", label: t.amount },
                    { value: "percentage", label: t.percentage },
                  ]}
                  onChange={(e) => updatePricing(idx, { discount: e.target.value === "none" ? null : { type: e.target.value, value: entry.discount?.value ?? 0 } })}
                />
                {entry.discount && (
                  <Input type="number" min="0" step="0.01" placeholder={t.discountValue} aria-label={t.discountValue} fullWidth={false} value={entry.discount.value ?? ""} onChange={(e) => updatePricing(idx, { discount: { ...entry.discount, value: e.target.value === "" ? "" : Number(e.target.value) } })} />
                )}
                <span className="food-effective-price" title={t.effective}>
                  {effective === null ? "—" : `${effective} ${isArabic ? "ر" : "SAR"}`}
                </span>
                <Button type="button" variant="danger" appearance="soft" size="sm" rounded icon={<Trash2 size={17} />} aria-label={isArabic ? "حذف السعر" : "Remove price"} onClick={() => removePricing(idx)} />
                {entryError && <small className="menu-error food-pricing-error">{entryError}</small>}
              </div>
            );
          })}
        </div>

        <div className={`food-form-section food-linked-additions-section ${isArabic ? "is-rtl" : "is-ltr"}`}>
          <div className="food-form-section-title">
            <div>
              <h4>{t.linkedAdditionsTitle}</h4>
              <p className="food-form-section-note">{t.linkedAdditionsHint}</p>
            </div>
          </div>

          {menuAdditions.length === 0 ? (
            <p className="food-additions-empty">{t.linkedAdditionsEmpty}</p>
          ) : (
            <>
              <SearchCheckList options={menuAdditionOptions} selected={selectedAdditionIds} onChange={updateLinkedAdditionGroups} placeholder={t.searchPlaceholder} fullWidth="full-width food-linked-additions-dropdown" />
              <div className="food-linked-additions-preview">
                <h5>{t.selectedGroups}</h5>
                {selectedMenuAdditions.length === 0 ? (
                  <p className="food-linked-additions-no-selected">{t.noSelectedGroups}</p>
                ) : (
                  <div className="food-linked-additions-selected-list">
                    {selectedMenuAdditions.map(({ group, groupIndex, addition }) => (
                      <div
                        className="food-linked-additions-selected-row"
                        key={group._id || `${addition._id}-${group.order}-${groupIndex}`}
                        draggable
                        onDragStart={() => setDraggedAdditionIndex(groupIndex)}
                        onDragOver={(event) => event.preventDefault()}
                        onDragEnter={(event) => {
                          event.preventDefault();
                          if (draggedAdditionIndex === null || draggedAdditionIndex === groupIndex) return;
                          moveLinkedAdditionGroup(draggedAdditionIndex, groupIndex);
                          setDraggedAdditionIndex(groupIndex);
                        }}
                        onDragEnd={() => setDraggedAdditionIndex(null)}
                      >
                        <div className="food-linked-additions-selected-head">
                          <div className="food-linked-additions-selected-title">
                            <GripVertical className="food-linked-additions-drag-handle" size={17} />
                            <strong>{getAdditionTitle(addition)}</strong>
                            <small className="menu-secondary">{addition.shortName}</small>
                          </div>
                          <div className="food-linked-additions-selected-actions">
                            <Button type="button" variant="primary" appearance="soft" size="sm" rounded icon={<Plus size={15} />} aria-label={isArabic ? "تكرار المجموعة" : "Repeat group"} onClick={() => duplicateLinkedAdditionGroup(groupIndex)} />
                            <Button type="button" variant="danger" appearance="soft" size="sm" rounded icon={<Trash2 size={15} />} aria-label={isArabic ? "إزالة المجموعة" : "Remove group"} onClick={() => removeLinkedAdditionGroup(groupIndex)} />
                            <span className="food-linked-additions-status">{addition.isMandatory ? t.mandatory : t.optional}</span>
                          </div>
                        </div>
                        {(addition.options || []).length > 0 && (
                          <div className="food-linked-additions-selected-options">
                            {(addition.options || []).map((option, index) => (
                              <small key={`${addition._id}-${index}`}>
                                {getOptionName(option)}
                                <b>{formatOptionPrice(option.price)}</b>
                              </small>
                            ))}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </>
          )}
        </div>
    </>
  );
}

export default function FoodItemForm({ itemData, setItemData, onSave, onCancel, mode = "add", menuAdditions = [], busy = false, error = "" }) {
  const { lang } = useContext(Context);
  const t = TEXT[lang] || TEXT.ar;
  const [localError, setLocalError] = useState("");

  const handleSave = () => {
    const problem = itemFormError(itemData, lang);
    setLocalError(problem);
    return problem ? undefined : onSave();
  };

  return (
    <div className="food-modal-overlay">
      <div className="food-modal food-item-form-modal" role="dialog" aria-modal="true" aria-busy={busy || undefined}>
        <h3>{mode === "edit" ? t.editTitle : t.addTitle}</h3>
        <FoodItemFields itemData={itemData} setItemData={setItemData} menuAdditions={menuAdditions} />
        {(localError || error) && (
          <p className="menu-error" role="alert">
            {localError || error}
          </p>
        )}
        <div className="food-modal-actions">
          <Button type="button" variant="primary" appearance="filled" fullWidth rounded disabled={busy} onClick={handleSave}>
            {t.save}
          </Button>
          <Button type="button" variant="warning" appearance="soft" fullWidth rounded disabled={busy} onClick={onCancel}>
            {t.cancel}
          </Button>
        </div>
      </div>
    </div>
  );
}
